import React from 'react';
import { Card, Row, Col, Progress, Typography, List, Tag } from 'antd';
import { TrophyOutlined, AlertOutlined, RocketOutlined } from '@ant-design/icons';
import AnalyticsCard from '../../components/shared/AnalyticsCard';
import { mockExecutiveSummary, mockPerformanceData } from '../data/mockData';

const { Text } = Typography;

const ExecutiveSummary: React.FC = () => {
  const { summary, comparisons } = mockPerformanceData;
  const { keyMetrics, achievements, challenges, priorities } = mockExecutiveSummary;

  const getPerformanceColor = (performance: number) => {
    if (performance >= 100) return '#52c41a';
    if (performance >= 90) return '#1890ff';
    if (performance >= 80) return '#faad14';
    return '#f5222d';
  };

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <AnalyticsCard
            title="Total Clients"
            value={summary.totalClients}
            format="number"
            trend="up"
            change={((summary.totalClients - 225) / 225) * 100}
            subtitle="vs last year"
          />
        </Col>
        <Col span={6}>
          <AnalyticsCard
            title="Active Coaches"
            value={summary.activeCoaches}
            format="number"
            trend="stable"
            change={0}
            subtitle="coaching staff"
          />
        </Col>
        <Col span={6}>
          <AnalyticsCard
            title="Completed Sessions"
            value={summary.completedSessions}
            format="number"
            trend="up"
            change={18.4}
            subtitle="vs last quarter"
          />
        </Col>
        <Col span={6}>
          <AnalyticsCard
            title="Satisfaction Score"
            value={summary.avgSatisfactionScore}
            format="number"
            trend="up"
            change={4.9}
            subtitle="out of 5.0"
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <AnalyticsCard
            title="Month-over-Month Growth"
            value={comparisons.monthOverMonth.percentage}
            format="percentage"
            trend={comparisons.monthOverMonth.change > 0 ? 'up' : 'down'}
            change={comparisons.monthOverMonth.percentage}
            subtitle="revenue growth"
          />
        </Col>
        <Col span={8}>
          <AnalyticsCard
            title="Year-over-Year Growth"
            value={comparisons.yearOverYear.percentage}
            format="percentage"
            trend="up"
            change={comparisons.yearOverYear.percentage}
            subtitle="revenue growth"
          />
        </Col>
        <Col span={8}>
          <AnalyticsCard
            title="Quarter-over-Quarter Growth"
            value={comparisons.quarterOverQuarter.percentage}
            format="percentage"
            trend={comparisons.quarterOverQuarter.change > 0 ? 'up' : 'down'}
            change={comparisons.quarterOverQuarter.percentage}
            subtitle="revenue growth"
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card title="Key Performance Indicators">
            <Row gutter={[16, 16]}>
              {keyMetrics.map((metric, index) => (
                <Col span={8} key={index}>
                  <Card size="small">
                    <div style={{ marginBottom: 12 }}>
                      <Text strong>{metric.metric}</Text>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <Text>Current: {typeof metric.current === 'number' && metric.current > 1000 ? `$${metric.current.toLocaleString()}` : metric.current}</Text>
                      <Text type="secondary">Target: {typeof metric.target === 'number' && metric.target > 1000 ? `$${metric.target.toLocaleString()}` : metric.target}</Text>
                    </div>
                    <Progress 
                      percent={metric.performance} 
                      strokeColor={getPerformanceColor(metric.performance)}
                      format={(percent) => `${percent?.toFixed(1)}%`}
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col span={8}>
          <Card
            title={
              <span>
                <TrophyOutlined style={{ color: '#52c41a' }} /> Key Achievements
              </span>
            }
            style={{ height: '300px' }}
          >
            <List
              size="small"
              dataSource={achievements}
              renderItem={(item) => (
                <List.Item>
                  <Tag color="success" style={{ marginRight: 8 }}>✓</Tag>
                  <Text>{item}</Text>
                </List.Item>
              )}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card
            title={
              <span>
                <AlertOutlined style={{ color: '#faad14' }} /> Current Challenges
              </span>
            }
            style={{ height: '300px' }}
          >
            <List
              size="small"
              dataSource={challenges}
              renderItem={(item) => (
                <List.Item>
                  <Tag color="warning" style={{ marginRight: 8 }}>!</Tag>
                  <Text>{item}</Text>
                </List.Item>
              )}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card
            title={
              <span>
                <RocketOutlined style={{ color: '#1890ff' }} /> Strategic Priorities
              </span>
            }
            style={{ height: '300px' }}
          >
            <List
              size="small"
              dataSource={priorities}
              renderItem={(item) => (
                <List.Item>
                  <Tag color="processing" style={{ marginRight: 8 }}>→</Tag>
                  <Text>{item}</Text>
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ExecutiveSummary; 